import { TOTP, Secret } from 'otpauth';

/**
 * Base32 alphabet for encoding
 */
const BASE32_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ234567';

/**
 * Encode bytes to base32 string
 * @param {Uint8Array} bytes - The bytes to encode
 * @returns {string} Base32 encoded string
 */
function base32Encode(bytes) {
  let result = '';
  let bits = 0;
  let value = 0;

  for (let i = 0; i < bytes.length; i++) {
    value = (value << 8) | bytes[i];
    bits += 8;

    while (bits >= 5) {
      result += BASE32_ALPHABET[(value >>> (bits - 5)) & 31];
      bits -= 5;
    }
  }

  if (bits > 0) {
    result += BASE32_ALPHABET[(value << (5 - bits)) & 31];
  }

  return result;
}

/**
 * Generate a cryptographically secure secret (160 bits = 20 bytes)
 * @returns {string} Base32 encoded secret (32 characters)
 */
export function generateSecret() {
  const bytes = new Uint8Array(20);
  crypto.getRandomValues(bytes);
  return base32Encode(bytes);
}

/**
 * Format secret for display with spaces every 4 characters
 * @param {string} secret - The base32 secret
 * @returns {string} Formatted secret for easy reading
 */
export function formatSecret(secret) {
  return secret.match(/.{1,4}/g)?.join(' ') || secret;
}

/**
 * Create TOTP instance for a pairing
 * @param {string} secret - Base32 encoded secret
 * @param {string} labelA - First person's name
 * @param {string} labelB - Second person's name
 * @returns {TOTP} TOTP instance
 */
export function createTOTP(secret, labelA, labelB) {
  return new TOTP({
    issuer: 'Handshake',
    label: `${labelA} ↔ ${labelB}`,
    algorithm: 'SHA1',
    digits: 6,
    period: 30,
    secret: Secret.fromBase32(secret),
  });
}

/**
 * Get current TOTP code
 * @param {string} secret - Base32 encoded secret
 * @returns {string} 6-digit code
 */
export function getCurrentCode(secret) {
  const totp = new TOTP({
    algorithm: 'SHA1',
    digits: 6,
    period: 30,
    secret: Secret.fromBase32(secret),
  });
  return totp.generate();
}

/**
 * Get seconds remaining in current TOTP period
 * @returns {number} Seconds remaining (0-30)
 */
export function getSecondsRemaining() {
  return 30 - (Math.floor(Date.now() / 1000) % 30);
}

/**
 * Generate otpauth:// URI for authenticator apps
 * @param {string} secret - Base32 encoded secret
 * @param {string} labelA - First person's name
 * @param {string} labelB - Second person's name
 * @returns {string} otpauth:// URI
 */
export function getOTPAuthURI(secret, labelA, labelB) {
  const totp = createTOTP(secret, labelA, labelB);
  return totp.toString();
}

/**
 * Format code with space in middle for display
 * @param {string} code - 6-digit code
 * @returns {string} Formatted code (e.g., "384 729")
 */
export function formatCode(code) {
  return code.slice(0, 3) + ' ' + code.slice(3);
}
